import { createContext } from 'react';
import { FormattedMessage } from 'react-intl';

import { isNotNullish } from '@app/helpers/predicate';
import useSimpleCart, { getCartData } from '@app/hooks/useSimpleCart';

import { AlertTitle, AlertType } from '../components/Alert';
import type { AlertItem } from '../hooks/useAlerts';
import useAlerts from '../hooks/useAlerts';
import messages from './CartAlertsContext.messages';

export interface CartAlertsContextValue {
  alerts: AlertItem[];
  clearCartAlerts: () => void;
  addCartAlerts: (alerts: AlertItem[]) => void;
}

const initialValue: CartAlertsContextValue = {
  alerts: [],
  addCartAlerts: () => undefined,
  clearCartAlerts: () => undefined,
};

export const CartAlertsContext =
  createContext<CartAlertsContextValue>(initialValue);
CartAlertsContext.displayName = 'CartAlertsContext';

export const CartAlertsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { alerts, clearAlerts, addAlerts } = useAlerts([]);
  useSimpleCart({
    onCompleted(data) {
      const { items } = getCartData(data);
      const cartItems = items?.filter(isNotNullish) ?? null;
      const localItems = localStorage.getItem('localItemsCart');

      let localItemsCart: string[] = [];

      if (localItems) {
        const localItemsArray: string[] = JSON.parse(localItems);

        localItemsCart = localItemsArray;
      }

      if (cartItems) {
        items?.forEach((item) => {
          if (!item?.product?.uid) return;

          if (!localItemsCart.includes(item.product.uid)) {
            localItemsCart.push(item.product.uid);
            addAlerts([
              {
                type: AlertType.Success,
                content: (
                  <AlertTitle>
                    {item?.product.name}{' '}
                    <FormattedMessage {...messages.newItemAdded} />
                  </AlertTitle>
                ),
              },
            ]);
            localStorage.setItem(
              'localItemsCart',
              JSON.stringify(localItemsCart),
            );
          }
        });
      }
    },
    onError: (error) => {
      clearAlerts();
      addAlerts([
        {
          type: AlertType.Error,
          content: <AlertTitle>{error.message}</AlertTitle>,
        },
      ]);
    },
  });
  return (
    <CartAlertsContext.Provider
      value={{
        addCartAlerts: addAlerts,
        clearCartAlerts: clearAlerts,
        alerts,
      }}
    >
      {children}
    </CartAlertsContext.Provider>
  );
};

// [WWW-9480] TODO: Remove this mock, created to prevent SSR errors
const localStorage =
  typeof window !== 'undefined'
    ? window.localStorage
    : {
        getItem: () => {},
        setItem: () => {},
      };
